import { useState } from "react";
import { ChevronDown, SlidersHorizontal } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { CreditCostPill } from "@/components/flowly/CreditCostPill/CreditCostPill";
import { TALKING_AVATAR_MAX_BG_CHARS } from "@/pages/TalkingAvatar/constants";

interface TalkingAvatarBackgroundInputProps {
  /** Display number — shifts with the chosen image mode path. */
  stepNumber: number;
  background: string;
  composition: string;
  isGenerating: boolean;
  hasPreview: boolean;
  /** Credit cost for the preview pair — shown next to the CTA so the user
   *  knows exactly what each click costs. */
  previewCost: number;
  onBackgroundChange: (value: string) => void;
  onCompositionChange: (value: string) => void;
  onGenerate: () => void;
}

export function TalkingAvatarBackgroundInput({
  stepNumber,
  background,
  composition,
  isGenerating,
  hasPreview,
  previewCost,
  onBackgroundChange,
  onCompositionChange,
  onGenerate,
}: TalkingAvatarBackgroundInputProps) {
  const { t } = useLanguage();
  // Auto-open the advanced panel if the user already typed something there
  // (e.g. coming back to this step after picking a preview).
  const [advancedOpen, setAdvancedOpen] = useState(composition.length > 0);

  return (
    <div className="space-y-4 md:space-y-5">
      <div>
        <h3 className="text-base md:text-lg font-semibold text-foreground">
          {t.talkingAvatar.stepPrefix.replace("{n}", String(stepNumber))}
          {t.talkingAvatar.backgroundStepTitle}
        </h3>
        <p className="text-xs md:text-sm text-muted-foreground mt-0.5">
          {t.talkingAvatar.backgroundStepHint}
        </p>
      </div>

      <div>
        <label className="block mb-2 text-sm font-medium text-foreground">
          {t.talkingAvatar.backgroundLabel}
        </label>
        <textarea
          value={background}
          onChange={(e) => onBackgroundChange(e.target.value)}
          placeholder={t.talkingAvatar.backgroundPlaceholder}
          rows={3}
          maxLength={TALKING_AVATAR_MAX_BG_CHARS}
          className="w-full rounded-xl border border-border bg-card px-4 py-3 text-sm md:text-base text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors resize-none"
        />
        <p className="mt-1.5 text-[11px] text-muted-foreground/80 text-end">
          {background.length} / {TALKING_AVATAR_MAX_BG_CHARS}
        </p>

        <div className="mt-3">
          <p className="text-[11px] text-muted-foreground/80 mb-2">
            {t.talkingAvatar.backgroundPresetsLabel}
          </p>
          <div className="flex flex-wrap gap-2">
            {[
              { label: t.talkingAvatar.backgroundPresetOfficeLabel, text: t.talkingAvatar.backgroundPresetOfficeText },
              { label: t.talkingAvatar.backgroundPresetForestLabel, text: t.talkingAvatar.backgroundPresetForestText },
              { label: t.talkingAvatar.backgroundPresetHomeLabel, text: t.talkingAvatar.backgroundPresetHomeText },
              { label: t.talkingAvatar.backgroundPresetKitchenLabel, text: t.talkingAvatar.backgroundPresetKitchenText },
            ].map((preset) => (
              <button
                key={preset.label}
                type="button"
                onClick={() => onBackgroundChange(preset.text)}
                className="px-3 py-1.5 rounded-full border border-border bg-card hover:border-primary/60 hover:bg-primary/5 text-xs md:text-sm text-foreground transition-colors active:scale-95"
              >
                {preset.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div>
        <button
          type="button"
          onClick={() => setAdvancedOpen((v) => !v)}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
          aria-expanded={advancedOpen}
        >
          <SlidersHorizontal className="w-4 h-4" />
          <span>
            {advancedOpen
              ? t.talkingAvatar.advancedToggleHide
              : t.talkingAvatar.advancedToggleShow}
          </span>
          <ChevronDown
            className={cn(
              "w-4 h-4 transition-transform",
              advancedOpen && "rotate-180",
            )}
          />
        </button>
        {advancedOpen && (
          <div className="mt-3 rounded-xl border border-border/60 bg-card/50 p-3 md:p-4 animate-in fade-in slide-in-from-top-1 duration-200">
            <label className="block mb-2 text-sm font-medium text-foreground">
              {t.talkingAvatar.advancedCompositionLabel}
            </label>
            <textarea
              value={composition}
              onChange={(e) => onCompositionChange(e.target.value)}
              placeholder={t.talkingAvatar.advancedCompositionPlaceholder}
              rows={3}
              maxLength={TALKING_AVATAR_MAX_BG_CHARS}
              className="w-full rounded-xl border border-border bg-card px-4 py-3 text-sm md:text-base text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors resize-none"
            />
            <div className="mt-1.5 flex justify-between text-[11px] text-muted-foreground/80">
              <span>{t.talkingAvatar.advancedCompositionHint}</span>
              <span>
                {composition.length} / {TALKING_AVATAR_MAX_BG_CHARS}
              </span>
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-center">
        <button
          type="button"
          onClick={onGenerate}
          disabled={isGenerating}
          className={cn(
            // Neutral gray on purpose — this is an intermediate "Stage 1"
            // CTA. The bold gradient is reserved for the final render
            // button so the hierarchy reads: gray = "next step",
            // gradient = "finish".
            "inline-flex items-center gap-2 px-5 md:px-6 h-11 md:h-12 rounded-xl bg-zinc-800 hover:bg-zinc-900 text-white text-sm md:text-base font-semibold transition-colors shadow-sm",
            isGenerating && "opacity-60 cursor-wait hover:bg-zinc-800",
          )}
        >
          {isGenerating ? (
            <span>{t.talkingAvatar.previewGenerating}</span>
          ) : (
            <>
              <span>
                {hasPreview
                  ? t.talkingAvatar.previewRegenerateCta
                  : t.talkingAvatar.createCharactersCta}
              </span>
              <CreditCostPill cost={previewCost} />
            </>
          )}
        </button>
      </div>
    </div>
  );
}
